import React from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import {
  CalendarMonth as CalendarMonthIcon,
  ErrorOutlined as ErrorOutlineIcon,
  CheckCircleOutlineOutlined as CheckCircleOutlineIcon,
  HourglassEmptyOutlined as HourglassIcon,
} from '@mui/icons-material';
import { CaseStatusData, CaseStatusType } from '../types/CaseStatusData';
import { useCaseStatusLanguage } from '../../../../routes/CaseStatus/CaseStatusLanguageProvider';

type Props = { data: CaseStatusData };

const STATUS_CHIP: Record<
  CaseStatusType,
  { labelKey: string; color: 'secondary' | 'amber' | 'success'; Icon: typeof ErrorOutlineIcon }
> = {
  Open:              { labelKey: 'Active', color: 'secondary', Icon: ErrorOutlineIcon },
  PaymentProcessing: { labelKey: 'ProcessingPayment', color: 'amber', Icon: HourglassIcon },
  PaymentAllocated:  { labelKey: 'Active', color: 'secondary', Icon: ErrorOutlineIcon },
  Closed:            { labelKey: 'Closed', color: 'success',    Icon: CheckCircleOutlineIcon },
};

const StatusCard = ({ data }: Props) => {
  const theme = useTheme();
  const { t, fmtDate } = useCaseStatusLanguage();

  const chip = STATUS_CHIP[data.status] ?? STATUS_CHIP.Open;
  const color = theme.palette[chip.color].main;
  const Icon = chip.Icon;

  return (
    <Paper elevation={1} sx={{ borderLeft: `6px solid ${color}`, p: 0, overflow: 'hidden', }} data-testid="case-status-card" >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', p: '20px', }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('Status')}
          </Typography>
          <Box
            data-testid="case-status-chip"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.5,
              px: 1,
              py: 0.25,
              border: `1px solid ${color}55`,
              borderRadius: 999,
              color,
              backgroundColor: `${color}14`,
            }}
          >
            <Icon sx={{ fontSize: 16 }} />
            <Typography component="span" sx={{ color, fontWeight: 700, lineHeight: 1.6 }}>
              {t(chip.labelKey)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarMonthIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">{t('RegDate')}</Typography>
          </Box>
          <Typography sx={{ fontWeight: 700, mt: 0.25 }}>
            {fmtDate(data.registrationDate)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default StatusCard;
